import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import { Col, Form, PageHeader, Row, Select, Table } from "antd";
import {
	SortableContainer,
	SortableElement,
	SortableHandle,
} from "react-sortable-hoc";

import { GetUrlParams, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import API from "scripts/api";
import Resources from "scripts/resources";

const DragHandle = SortableHandle(() => <i className="fa fa-bars" />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);
const SortableItem = SortableElement((props) => <tr {...props} />);

const Component = ({ onSetRoute, parentName, routeName }) => {
	const initialState = { courses: [], data: [] };
	const tableColumns = [
		{
			title: Resources.sort,
			dataIndex: "sort",
			className: "center-cell drag-cell",
			width: 100,
			render: () => <DragHandle />,
		},
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: 60,
			render: (value, record, index) => index + 1,
		},
		{
			title: Resources.title,
			dataIndex: "title",
		},
	];
	const [state, setState] = useState(initialState);
	const { courses, data, loading } = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const handleSort = ({ oldIndex, newIndex }) => {
		if (oldIndex !== newIndex) {
			setState((prevState) => ({ ...prevState, loading: true }));

			const item = data.find((item, index) => index === oldIndex);
			const list = data.filter((item, index) => index !== oldIndex);

			list.splice(newIndex, 0, item);

			API.session
				.reorder({ ids: list.map((item) => item.id) })
				.then(() => {
					setState((prevState) => ({
						...prevState,
						data: list,
						loading: false,
					}));
				})
				.catch(() => {
					setState((prevState) => ({ ...prevState, loading: false }));
				});
		}
	};

	const DraggableContainer = (props) => (
		<SortableBody
			useDragHandle
			disableAutoscroll
			helperClass="sp-row-dragging"
			onSortEnd={handleSort}
			{...props}
		/>
	);

	const DraggableBodyRow = ({ className, style, ...restProps }) => {
		const index = data.findIndex((x) => x.index === restProps["data-row-key"]);
		return <SortableItem index={index} {...restProps} />;
	};

	const fetchData = () => {
		const { course } = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue({ course });

		if (course) {
			setState((prevState) => ({ ...prevState, loading: true }));

			API.session
				.list({ course, status: true, paginate: false })
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						data: data.map((item, index) => ({ ...item, index })),
						loading: false,
					}));
				})
				.catch(() => {
					setState((prevState) => ({ ...prevState, loading: false }));
				});
		} else {
			setState((prevState) => ({ ...prevState, data: [] }));
		}
	};

	const handleFilter = debounce(() => {
		form.validateFields().then(({ course }) => {
			const search = ObjectToURL({ course });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });

		API.course
			.list({ status: true, paginate: false })
			.then(({ data }) => {
				setState((prevState) => ({
					...prevState,
					courses: data.map(({ id, title }) => ({ value: id, label: title })),
				}));
			})
			.catch(() => {});
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader title={Resources.sessions} />

			<Form layout="vertical" form={form}>
				<Row gutter={24}>
					<Col xs={24} sm={12} md={8} lg={6} xxl={4}>
						<Form.Item name="course">
							<Select
								onChange={handleFilter}
								options={courses}
								placeholder={Resources.course}
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				pagination={false}
				rowKey="index"
				components={{
					body: { wrapper: DraggableContainer, row: DraggableBodyRow },
				}}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
